import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  SeatCushionTemplate,
  CreateSeatCushionTemplate,
  UpdateSeatCushionTemplate,
} from '@estimator/shared';

export function useSeatCushionTemplates() {
  return useQuery({
    queryKey: ['seat-cushion-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<SeatCushionTemplate[]>('/banquette-seat-cushion-templates');
      return data;
    },
  });
}

export function useSeatCushionTemplate(id: string | null) {
  return useQuery({
    queryKey: ['seat-cushion-templates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<SeatCushionTemplate>(
        `/banquette-seat-cushion-templates/${id}`,
      );
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateSeatCushionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSeatCushionTemplate) => {
      const { data } = await apiClient.post<SeatCushionTemplate>(
        '/banquette-seat-cushion-templates',
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seat-cushion-templates'] });
    },
  });
}

export function useUpdateSeatCushionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateSeatCushionTemplate & { id: string }) => {
      const { data } = await apiClient.patch<SeatCushionTemplate>(
        `/banquette-seat-cushion-templates/${id}`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['seat-cushion-templates'] });
      queryClient.invalidateQueries({ queryKey: ['seat-cushion-templates', vars.id] });
    },
  });
}

export function useDeleteSeatCushionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/banquette-seat-cushion-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seat-cushion-templates'] });
    },
  });
}
